/**
 * POTransactionsTab — Tab 2 on PurchaseOrdersPage
 * Shows all PO transactions across all inventory types.
 * "Create PO Transaction" opens POTransactionsStepper.
 * "..." → "Confirm Delivery" opens ConfirmDeliveryDialog.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, InputAdornment,
  Alert, Snackbar, alpha, useTheme, FormControl,
  InputLabel, Select, MenuItem, Button
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useLocations } from '../../inventory/inventory-components/shared/useInventoryData';
import { fetchPOTransactions, deletePOTransaction, cleanupDeliveredTransaction } from './poTransactionHelpers';
import { supabase } from '../../../supabaseClient';
import POTransactionsStepper from './POTransactionsStepper';
import ConfirmGroupDeliveryDialog from './ConfirmGroupDeliveryDialog';
import POTransactionTable from './POTransactionTable';

const ALL_TYPES = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];

const selectSx = (theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: 'white',
  minHeight: 56,
  '& .MuiSelect-select': { py: 1.75, px: 2, fontSize: '0.9rem', fontWeight: 500, minHeight: 'auto', display: 'flex', alignItems: 'center' },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` },
  '&.Mui-focused': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}` }
});

const labelSx = {
  fontSize: '0.9rem', fontWeight: 500,
  transform: 'translate(14px, 16px) scale(1)',
  '&.Mui-focused, &.MuiFormLabel-filled': { fontSize: '0.9rem', transform: 'translate(14px, -9px) scale(0.75)' }
};

function getItemName(tx) {
  const map = {
    products: tx.products?.name, chemicals: tx.raw_materials?.name,
    caps: tx.caps?.name, bottles: tx.bottles?.name, labels: tx.labels?.name, supplies: tx.supplies?.name,
  };
  return map[tx._type] || '—';
}

export default function POTransactionsTab({ companyId, stepperOpen: externalStepperOpen, onStepperOpenChange }) {
  const theme = useTheme();
  const { locations } = useLocations(companyId);

  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);
  const [_stepperOpen, _setStepperOpen] = useState(false);
  const stepperOpen    = externalStepperOpen !== undefined ? externalStepperOpen : _stepperOpen;
  const setStepperOpen = onStepperOpenChange ?? _setStepperOpen;
  const [confirmGroup, setConfirmGroup] = useState(null); // array of transactions
  const [snackbar,     setSnackbar]     = useState(null);

  const handleDelete = async (tx) => {
    try {
      await cleanupDeliveredTransaction(companyId, tx);
      await deletePOTransaction(tx._type, tx.id);

      // If no transactions remain for this PO, revert PO status to 'draft'
      if (tx.po_number) {
        const remaining = transactions.filter(t => t.id !== tx.id && t.po_number === tx.po_number);
        if (remaining.length === 0) {
          await supabase
            .from('vendor_PO')
            .update({ status: 'draft' })
            .eq('company_id', companyId)
            .eq('vendor_PO_number', tx.po_number);
        }
      }

      showSnack('Transaction deleted.');
      load();
    } catch (e) {
      showSnack(`Delete failed: ${e.message}`, 'error');
    }
  };
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchPOTransactions(companyId);
      setTransactions(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const isDefaultFilters = searchTerm === '' && typeFilter === 'all' && statusFilter === 'all';
  const resetFilters = () => { setSearchTerm(''); setTypeFilter('all'); setStatusFilter('all'); };

  const filtered = useMemo(() => {
    setPage(0);
    const q = searchTerm.toLowerCase().trim();
    return transactions.filter(tx => {
      if (typeFilter !== 'all' && tx._type !== typeFilter) return false;
      if (statusFilter === 'pending'   && tx.delivery_status !== 'ordered')   return false;
      if (statusFilter === 'delivered' && tx.delivery_status !== 'delivered') return false;
      if (q) {
        const itemName = getItemName(tx).toLowerCase();
        const poNum    = (tx.po_number || '').toLowerCase();
        if (!itemName.includes(q) && !poNum.includes(q)) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, searchTerm, typeFilter, statusFilter]);

  const showSnack = (msg, severity = 'success') => setSnackbar({ msg, severity });

  return (
    <Box>
      <Snackbar
        open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'success'} onClose={() => setSnackbar(null)} sx={{ borderRadius: 2 }}>
          {snackbar?.msg}
        </Alert>
      </Snackbar>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* Filter Paper */}
      <Paper sx={{
        p: 4, mb: 3, borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: 'primary.main', fontSize: '1.25rem' }}>
          Filter & Search PO Transactions
        </Typography>

        <Grid container spacing={3} alignItems="flex-end">
          {/* Search */}
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Search by PO number or item..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` },
                  '&.Mui-focused': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}` }
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } } }}
              size="medium"
            />
          </Grid>

          {/* Inventory Type */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={labelSx}>Inventory Type</InputLabel>
              <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} label="Inventory Type" sx={selectSx(theme)}>
                <MenuItem value="all" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>All Types</MenuItem>
                {ALL_TYPES.map(t => (
                  <MenuItem key={t} value={t} sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={labelSx}>Status</InputLabel>
              <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status" sx={selectSx(theme)}>
                <MenuItem value="all"       sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>All Status</MenuItem>
                <MenuItem value="pending"   sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Pending</MenuItem>
                <MenuItem value="delivered" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Delivered</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Reset */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth size="medium"
              onClick={resetFilters}
              disabled={isDefaultFilters}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                height: 56,
                px: 3,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Reset
            </Button>
          </Grid>

          {/* Count */}
          <Grid item xs={12} md={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 56 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
                {filtered.length} found
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <POTransactionTable
        transactions={filtered}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}

        onConfirmGroup={items => setConfirmGroup(items)}
        onDelete={handleDelete}
      />

      <POTransactionsStepper
        open={stepperOpen}
        onClose={() => setStepperOpen(false)}
        companyId={companyId}
        locations={locations || []}
        onSuccess={(msg, severity = 'success') => { showSnack(msg, severity); load(); }}
      />

      {confirmGroup && (
        <ConfirmGroupDeliveryDialog
          open={!!confirmGroup}
          onClose={() => setConfirmGroup(null)}
          transactions={confirmGroup}
          companyId={companyId}
          onSuccess={() => { setConfirmGroup(null); showSnack('All items confirmed — inventory updated'); load(); }}
        />
      )}
    </Box>
  );
}
